<template>
    <div class="portfolio-edit">
        <div class="row">
            <div class="col-12 col-md-6 xxl-3 portfolio-categories-wrapper">
                <div class="portfolio-categories apprentice-simple-card">
                    <div class="component-title">
                        <h5 class="title">
                            {{ $t('portfolio.categories') }}
                        </h5>
                    </div>
                    <b-list-group class="portfolio-category-list"
                                  v-if="lessonModuleIn.portfolio.categories.length">
                        <draggable
                            group="categories"
                            ghost-class="drag-ghost"
                            :list="portfolio.categories"
                            handle=".category-handle"
                            @change="categoryOrderChanged()">
                            <b-list-group-item
                                class="portfolio-category-list-item"
                                v-for="(cat, catKey) in lessonModuleIn.portfolio.categories"
                                :key="catKey"
                                @click="updateSelectedPortfolio(cat.id)"
                                active-class="active"
                                :active="cat.id == selectedPortfolioCategory">
                                <div class="category-item">
                                    <div class="move drag-icon"></div>
                                    <div class="category-name">
                                        <span class="drag-button">
                                            <b-button
                                                class="btn-drag-icon btn-sm"
                                                variant="none"
                                                @click="$event.stopPropagation()">
                                                <i class="fas fa-fw fa-arrows-alt-v category-handle"></i>
                                            </b-button>
                                        </span>
                                        {{ cat.name }}
                                    </div>

                                    <div class="context-nav portfolio-category-item-context-nav">
                                        <b-dropdown
                                            class="ellipsis-nav portfolio-category-context-menu"
                                            variant="primary"
                                            no-caret
                                            right>
                                            <template #button-content>
                                                <i class="fas fa-fw fa-ellipsis-v"></i>
                                            </template>
                                            <b-dropdown-item
                                                class="btn-edit nav-context-link-edit"
                                                @click="editCategoryModal(cat)">
                                                {{ $t('portfolio.edit_category') }}
                                            </b-dropdown-item>
                                            <b-dropdown-item
                                                class="nav-context-link-remove"
                                                v-b-tooltip="$t('portfolio.delete_category')"
                                                @click="removeCategory(cat, $event)"
                                                variant="inverse-danger">
                                                <i class="btn-remove"></i>{{ $t('portfolio.delete_category') }}
                                            </b-dropdown-item>
                                        </b-dropdown>
                                    </div>
                                </div>
                            </b-list-group-item>
                        </draggable>
                    </b-list-group>

                    <div
                        class="not-found with-header empty-portfolio-category-list"
                        v-else>
                        <div
                            class="btn-add-circle icon"
                            v-b-tooltip.hover
                            @click="showAddCategoryModal"
                            :title="$t('portfolio.add_category')"></div>
                        <div class="alert-notice">{{ $t('portfolio.has_no_categories') }}</div>
                    </div>


                    <div class="add-category">
                        <b-button
                            class="btn-recycle btn-restore-portfolio-category"
                            variant="outline-primary"
                            :disabled="lessonModuleIn.portfolio.categories.length === 0"
                            @click="showRestorePortfolioCategoryModal">
                            {{ $t('portfolio.recover_category') }}
                        </b-button>

                        <b-button
                            v-if="lessonModuleIn.portfolio.categories.length"
                            class="btn-add btn-new-portfolio-category"
                            variant="primary"
                            @click="showAddCategoryModal">
                            {{ $t('portfolio.new_category') }}
                        </b-button>
                    </div>
                </div>
            </div>

            <div class="col-12 col-md-6 xxl-9 portfolio-assignments-wrapper">
                <div class="portfolio-assignments apprentice-simple-card">
                    <div class="component-title">
                        {{ $t('portfolio.assignments') }}
                    </div>

                    <b-list-group
                        class="portfolio-assignments-list"
                        v-if="getSelectedAssignments.length">
                        <draggable
                            group="assignments"
                            ghost-class="drag-ghost"
                            :list="getSelectedAssignments"
                            handle=".assignment-handle"
                            @change="assignmentOrderChanged(getSelectedAssignments)">
                            <b-list-group-item
                                class="portfolio-assignments-list-item"
                                v-for="(assignment, assKey) in getSelectedAssignments"
                                :key="assignment.id"
                                @click="editAssignment(assignment.id)">
                                <div class="assignment">
                                    <div class="drag order-1 order-xl-0">
                                        <b-button
                                            variant="none"
                                            @click="$event.stopPropagation()">
                                            <i class="fas fa-fw fa-arrows-alt-v assignment-handle"></i>
                                        </b-button>
                                    </div>

                                    <div class="content order-2 order-xl-1">
                                        <div class="header">
                                            <div class="title">
                                                <div class="name">
                                                    {{ assignment.name }}
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                            class="sub-assignments"
                                            v-if="assignment.sub_assignments.length">
                                            {{
                                                `${assignment.sub_assignments.length} ${
                                                    assignment.sub_assignments.length === 1
                                                        ? $t('portfolio.partial_assignment')
                                                        : $t('portfolio.partial_assignments')
                                                }`
                                            }}
                                        </div>

                                        <div
                                            class="sub-assignments"
                                            v-else>
                                            {{ $t('portfolio.no_assignments_in_this_category_yet') }}
                                        </div>
                                    </div>

                                    <div class="management-options order-0 order-xl-2">
                                        <div class="context-nav portfolio-assignments-item-context-nav">
                                            <b-dropdown
                                                class="ellipsis-nav portfolio-assignment-context-menu"
                                                variant="primary"
                                                no-caret
                                                right>
                                                <template #button-content>
                                                    <i class="fas fa-fw fa-ellipsis-v"></i>
                                                </template>
                                                <b-dropdown-item
                                                    class="nav-context-link-remove"
                                                    @click.prevent.stop="removeAssignment(assignment.id)">
                                                    <i class="btn-remove"></i>{{ $t('portfolio.delete_assignment') }}
                                                </b-dropdown-item>
                                            </b-dropdown>
                                        </div>
                                    </div>
                                </div>
                            </b-list-group-item>
                        </draggable>
                    </b-list-group>

                    <div
                        class="not-found with-header empty-portfolio-category-list"
                        v-else>
                        <div
                            class="fas fa-exclamation-circle icon"
                            v-if="lessonModuleIn.portfolio.categories.length === 0">
                        </div>

                        <div
                            v-else
                            class="btn-add-circle icon"
                            v-b-tooltip.hover
                            @click="showAddAssignmentModal"
                            :title="$t('portfolio.add_assignment')">
                        </div>

                        <div class="alert-notice">{{ $t('portfolio.this_category_has_no_assignments_yet') }}</div>
                    </div>

                    <div class="new-assignments">
                        <b-button
                            class="btn-recycle btn-restore-assignment"
                            variant="outline-primary"
                            :disabled="getSelectedAssignments.length === 0"
                            @click="showRestoreAssignmentModal">
                            {{ $t('portfolio.restore_assignment') }}
                        </b-button>

                        <b-button
                            class="btn-add btn-new-assignment"
                            v-if="getSelectedAssignments.length >= 1"
                            variant="primary"
                            @click="showAddAssignmentModal">
                            {{ $t('portfolio.add_assignment') }}
                        </b-button>
                    </div>
                </div>
            </div>
        </div>
        <design-2021-lesson-module-portfolio-add-category-modal-component
            ref="add-category-modal"
            @category-added="addCategory"
            :portfolio-in="lessonModuleIn.portfolio">
        </design-2021-lesson-module-portfolio-add-category-modal-component>
        <design-2021-lesson-module-portfolio-edit-category-modal-component
            ref="edit-category-modal"
            @category-updated="updateCategory"
            :portfolio-in="lessonModuleIn.portfolio">
        </design-2021-lesson-module-portfolio-edit-category-modal-component>

        <design-2021-portfolio-edit-restore-category-modal-component
            :portfolio-in="lessonModuleIn.portfolio"
            @category-restored="restoreCategory($event)"
            ref="restore-category-modal">
        </design-2021-portfolio-edit-restore-category-modal-component>

        <design-2021-lesson-module-portfolio-add-new-assignment-modal-component
            ref="add-assignment-modal"
            :category-in="getSelectedCategory()"
            @category-updated="updateCategory"
            :selected-category-id-in="selectedPortfolioCategory"
            :portfolio-in="lessonModuleIn.portfolio">
        </design-2021-lesson-module-portfolio-add-new-assignment-modal-component>

        <design-2021-portfolio-edit-restore-assignment-modal-component
            :portfolio-in="lessonModuleIn.portfolio"
            :selected-category-id-in="selectedPortfolioCategory"
            @assignment-restored="restoreAssignment($event)"
            ref="restore-assignment-modal">
        </design-2021-portfolio-edit-restore-assignment-modal-component>
    </div>
</template>

<script>
    import draggable from 'vuedraggable';
    import swal from 'sweetalert';

    export default {
        name: 'design-2021-lesson-module-portfolio-edit-component',
        props: {
            lessonModuleIn: {
                type: Object,
                required: true
            }
        },
        components: {
            draggable,
            swal
        },
        data() {
            return {
                selectedPortfolioCategory: this.lessonModuleIn.portfolio.categories[0]?.id,
                portfolio: this.lessonModuleIn.portfolio,
                categoryConnections: [
                    { type: 'assignments', name: this.$t('portfolio.assignments') },
                    { type: 'subAssignments', name: this.$t('portfolio.partial_assignments') }
                ],
                removedAssignment: null,
                categories: [],
                availableTags: [],
                bgColor: ''
            };
        },
        methods: {
            addCategory(category) {
                this.portfolio.categories.push(category);
                this.selectedPortfolioCategory = category.id;
                this.alert.success();
            },
            updateCategory(newCategory) {
                let assignmentLength = this.getSelectedAssignments.length;
                this.portfolio.categories = this.portfolio.categories.map((oldCategory) => {
                    if (oldCategory.id === newCategory.id) {
                        return newCategory;
                    }
                    return oldCategory;
                });
                this.alert.success();

                if (this.getSelectedAssignments.length > assignmentLength) {
                    this.editAssignment(this.getSelectedAssignments[this.getSelectedAssignments.length - 1].id);
                }
            },

            editCategoryModal(category) {
                this.$refs['edit-category-modal'].show(category);
            },

            restoreAssignment(assignment) {
                this.portfolio.categories
                    .find((category) => category.id === this.selectedPortfolioCategory)
                    .assignments.push(assignment);
            },

            restoreCategory(category) {
                this.portfolio.categories.push(category);
                this.categoryOrderChanged();
            },

            updateSelectedPortfolio(id) {
                const name = 'portfolio-show-selected-category-' + this.lessonModuleIn.portfolio.id;
                this.selectedPortfolioCategory = id;
                localStorage.setItem(name, id);
            },

            removeCategory(category, event) {
                event.stopPropagation();

                this.alert
                    .confirm(
                        this.$t('common.delete_item', { item: category.name }),
                        this.$t('portfolio.are_you_sure_you_want_to_delete_this_category')
                    )
                    .then((confirm) => {
                        if (confirm) {
                            this.alert.saving(
                                this.$t('portfolio.delete_category'),
                                this.$t('portfolio.the_category_will_be_deleted_please_wait_a_moment')
                            );

                            if (this.selectedPortfolioCategory && this.selectedPortfolioCategory === category.id) {
                                if (this.portfolio.categories.length > 1) {
                                    let newSelectedCategory = this.portfolio.categories.find(
                                        (cat) => cat.id !== category.id
                                    );
                                    this.selectedPortfolioCategory = newSelectedCategory.id;
                                    this.updateSelectedPortfolio(newSelectedCategory.id);
                                }
                            }

                            this.axios.delete(this.routes.newPortfolioCategoryDelete(this.portfolio.id, category.id)).then(
                                (response) => {
                                    this.portfolio.categories = this.portfolio.categories.filter(
                                        (category1) => category1.id !== category.id
                                    );

                                    this.alert.success();
                                },
                                (error) => {
                                    this.alert.ajaxError(error);
                                }
                            );
                        }
                    });
            },

            getSelectedCategory() {
                if (this.selectedPortfolioCategory !== null) {
                    for (const category of this.lessonModuleIn.portfolio.categories) {
                        if (category.id == this.selectedPortfolioCategory) {
                            return category;
                        }
                    }
                }
            },

            showAddCategoryModal() {
                this.$refs['add-category-modal'].show();
            },

            showRestorePortfolioCategoryModal() {
                this.$refs['restore-category-modal'].show();
            },

            showRestoreAssignmentModal() {
                this.$refs['restore-assignment-modal'].show();
            },

            showAddAssignmentModal() {
                this.$refs['add-assignment-modal'].show(this.selectedPortfolioCategory);
            },

            categoryOrderChanged() {
                let array = [];
                let count = 0;

                for (const category of this.portfolio.categories) {
                    count++;
                    array.push({
                        id: category.id,
                        order: count
                    });
                }

                const data = {
                    portfolio_id: this.portfolio.id,
                    blocks: array
                };

                this.axios.post(this.routes.portfolioCategoryOrder(), data).then(
                    (response) => {
                        // no action required
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    }
                );
            },

            assignmentOrderChanged(assignments) {
                let array = [];
                let counter = 0;
                for (let subAssignment of assignments) {
                    counter++;
                    array.push({
                        id: subAssignment.id,
                        order: counter
                    });
                }

                const data = {
                    category_id: this.selectedPortfolioCategory,
                    blocks: array
                };

                this.axios.post(this.routes.portfolioCategoryAssignmentOrder(), data).then(
                    (response) => {
                    }, // no action required
                    (error) => {
                        this.alert.ajaxError(error);
                    }
                );
            },

            editAssignment(assignmentId) {
                window.location.href = this.routes.organizationAssignment(this.lessonModuleIn.id, assignmentId);
            },

            removeAssignment(id) {
                this.alert
                    .confirm(
                        this.$t('portfolio.delete_assignment'),
                        this.$t('portfolio.are_you_sure_you_want_to_delete_this_assignment')
                    )
                    .then((confirm) => {
                        if (confirm) {
                            this.alert.saving();

                            this.axios.delete(this.routes.assignmentDelete(id)).then(
                                (response) => {
                                    this.portfolio.categories.map((category) => {
                                        category.assignments = category.assignments.filter(
                                            (assignment) => assignment.id !== id
                                        );
                                    });

                                    this.alert.success();
                                },
                                (error) => {
                                    this.alert.ajaxError(error);
                                }
                            );
                        }
                    });
            },

            dateFormat(unformattedDateString) {
                let dateObject = new Date(unformattedDateString);
                return new Intl.DateTimeFormat('nl', {
                    dateStyle: 'short',
                    timeStyle: 'short'
                }).format(dateObject);
            }
        },

        computed: {
            getSelectedAssignments() {
                if (this.selectedPortfolioCategory !== null) {
                    for (const category of this.portfolio.categories) {
                        if (category.id === this.selectedPortfolioCategory) {
                            return category.assignments;
                        }
                    }
                }

                return [];
            }
        }
    };
</script>

<style scoped lang="scss">
    .portfolio-categories {
        margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
    }

    .portfolio-category-list {
        .portfolio-category-list-item {
            padding: 0;
            border: none;

            .category-item {
                display: flex;
                align-items: center;
                padding: map-get($grid-gutter-spacing, 'eight');
                border-radius: $border-radius-content;
                margin-bottom: map-get($grid-gutter-spacing, 'ten');
                border: 1px solid $white;

                &:hover {
                    cursor: pointer;
                    border-radius: $border-radius-content;
                    border: 1px solid var(--primary);
                }
            }

            &:not(:first-child) {
                .category-item {
                    margin-top: map-get($grid-gutter-spacing, 'ten');
                }
            }

            &:not(:last-child) {
                border-bottom: 3px dotted $border-color;
            }

            &.active {
                background-color: transparent;

                .category-item {
                    background-color: $secondary-background-color;
                    font-weight: $font-weight-bold;
                    color: $text-color;
                }

                .btn-drag-icon {
                    color: $text-color;
                }
            }
        }

        .category-name {
            margin-right: map-get($grid-gutter-spacing, 'fifteen');
            flex: 1 1 0;
        }

        .btn-publish-assignments-toggle {
            margin-left: auto;
            margin-right: map-get($grid-gutter-spacing, 'fifteen');
            line-height: 1.5;
            font-size: $base-font-size;

            &:before {
                margin-right: 0;
            }

            &.disabled {
                &,
                &:hover {
                    background-color: darken($border-color, 10%) !important;

                    &:before {
                        color: $white;
                    }
                }
            }
        }

        .portfolio-category-item-context-nav ::v-deep {
            margin-right: map-get($grid-gutter-spacing, 'fifteen');

            .portfolio-category-context-menu {
                button.dropdown-toggle {
                    background-color: var(--primary);
                    color: $white !important;
                }

                .dropdown-menu {
                    border: 1px solid $border-color;
                }

                li.nav-context-link-rating,
                li.nav-context-link-edit,
                li.nav-context-link-remove {
                    &:before {
                        color: var(--primary);
                    }
                }

                li.nav-context-link-remove {
                    &:before {
                        color: $danger;
                    }
                }

                li.nav-context-link-remove a {
                    display: flex;
                    align-items: center;

                    i {
                        margin-right: map-get($grid-gutter-spacing, 'eight');
                    }
                }
            }
        }
    }

    .portfolio-assignments-list {
        .portfolio-assignments-list-item {
            border: none;
            padding-left: 0;
            padding-right: 0;

            &:first-child {
                padding-top: 0;
            }

            &:not(:first-child) {
                .assignment {
                    margin-top: map-get($grid-gutter-spacing, 'ten');
                }
            }

            &:not(:last-child) {
                border-bottom: 3px dotted $border-color;
            }
        }

        .assignment {
            display: flex;
            align-items: center;
            flex-direction: row;
            flex-wrap: wrap;
            padding-top: map-get($grid-gutter-spacing, 'eight');
            padding-bottom: map-get($grid-gutter-spacing, 'eight');
            border-radius: $border-radius-content;
            border: 1px solid $white;

            .management-options {
                display: flex;
                flex-direction: row;
                flex: 1 0 100%;
                margin-bottom: map-get($grid-gutter-spacing, 'ten');
                margin-right: map-get($grid-gutter-spacing, 'fifteen');
                justify-content: flex-end;

                .publish-option {
                    .btn-databank-share-create,
                    .btn-databank-share-edit {
                        font-size: $base-font-size;
                        line-height: 1.5;

                        &:before {
                            margin-right: 0;
                        }
                    }
                }

                .portfolio-assignments-item-context-nav ::v-deep {
                    margin-left: map-get($grid-gutter-spacing, 'fifteen');

                    .portfolio-assignment-context-menu {
                        button.dropdown-toggle {
                            background-color: var(--primary);
                            color: $white !important;
                        }

                        .dropdown-menu {
                            border: 1px solid $border-color;
                        }

                        li.nav-context-link-share,
                        li.nav-context-link-share,
                        .nav-context-link-update-selected-tag,
                        .nav-context-link-manage-tags,
                        .nav-context-link-manage-ratings,
                        .nav-context-link-manage-deadlines {
                            i,
                            &:before {
                                color: var(--primary);
                            }
                        }

                        li.nav-context-link-remove {
                            &:before {
                                color: $danger;
                            }
                        }

                        li.nav-context-link-somtoday a,
                        li.nav-context-link-remove a {
                            display: flex;
                            align-items: center;

                            i {
                                margin-right: map-get($grid-gutter-spacing, 'eight');
                            }
                        }
                    }
                }
            }

            .content {
                .header {
                    justify-content: flex-start;
                }

                .title {
                    display: flex;
                    flex-direction: column;
                    font-size: 1rem;
                    line-height: 1;
                    font-weight: $font-weight-bold;
                    row-gap: map-get($grid-gutter-spacing, 'four');
                    flex-wrap: wrap;
                    justify-self: flex-start;

                    .name {
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                        justify-content: flex-start;
                        column-gap: $grid-gutter-width;
                    }

                    .assignment-deadline {
                        color: $text-muted-color;
                    }
                }

                .sub-assignments {
                    color: $text-muted-color;
                }
            }

            &:not(:last-child) {
                border-bottom: 3px dotted $border-color;
            }

            &:hover {
                cursor: pointer;
                border-radius: $border-radius-content;
                border: 1px solid var(--primary);
                font-weight: $font-weight-bold;

                .sub-assignments {
                    font-weight: $font-weight-normal;
                }
            }
        }
    }

    .add-category,
    .new-assignments {
        display: flex;
        align-items: flex-start;
        flex-direction: column;
        row-gap: map-get($grid-gutter-spacing, 'twenty-one');
        margin-top: map-get($grid-gutter-spacing, 'forty-two');

        button {
            width: 100%;
        }
    }

    @media (min-width: map-get($grid-breakpoints, 'sm')) {
        .add-category,
        .new-assignments {
            justify-content: flex-start;
            flex-direction: row;
            column-gap: $grid-gutter-width;

            button {
                width: auto;
            }
        }
    }

    @media (min-width: map-get($grid-breakpoints, 'md')) {
        .portfolio-categories {
            margin-bottom: 0;
        }

        .portfolio-assignments,
        .portfolio-categories {
            min-height: 310px;
        }

        .add-category,
        .new-assignments {
            justify-content: flex-start;
            flex-direction: column;
            column-gap: 0;
        }
    }

    @media (min-width: map-get($grid-breakpoints, 'xl')) {
        .portfolio-assignments-list {
            .assignment {
                .management-options {
                    flex: 0 0 auto;
                    margin-bottom: 0;
                    margin-left: auto;
                    margin-right: 0;
                    align-self: flex-start;
                }

                .content {
                    max-width: calc(100% - 133px);

                    .title {
                        column-gap: map-get($grid-gutter-spacing, 'ten');

                        .assignment-deadline {
                            flex: 1 0 100%;
                        }
                    }
                }
            }
        }

        .portfolio-assignments-list-item {
            .assignment {
                padding-right: map-get($grid-gutter-spacing, 'fifteen');
            }
        }

        .portfolio-assignments,
        .portfolio-categories {
            min-height: 255px;
        }

        .add-category,
        .new-assignments {
            flex-direction: row;
            column-gap: map-get($grid-gutter-spacing, 'fifteen');
        }
    }

    @media (min-width: map-get($grid-breakpoints, 'xxl')) {
        .add-category,
        .new-assignments {
            flex-direction: row;
            column-gap: $grid-gutter-width;
        }
    }
</style>
